import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getStepFromPathname } from '../../shared/constants/flowConfig';
import { handleStepTransition } from './stepCheckpointService';
import { hydrateUndoManifests } from './undo/undoManifestSync';
import { flushStepCheckpointPushes } from './undo/checkpointAutoPush';

/**
 * Central step checkpoint coordinator. Runs on every design route change:
 * 1. Capture mesh + session for the step being left
 * 2. Restore mesh + session for the step being entered (if snapshot exists)
 *
 * Also owns two order-scoped concerns that have to happen exactly once per design
 * session: pulling the undo manifests down so Undo knows what it can do before the
 * user clicks it, and flushing any debounced checkpoint push if the tab closes.
 */
export function useStepCheckpointSync() {
  const location = useLocation();
  const previousStepRef = useRef(null);
  const transitionIdRef = useRef(0);

  // Undo material for the order. Without this the manifests written into
  // checkpoint metadata are never read back, so a second device starts blind and
  // every Undo button renders inert.
  useEffect(() => {
    void hydrateUndoManifests();
  }, []);

  // A debounced push must not be lost to a closing tab. `pagehide` fires in cases
  // `beforeunload` does not (mobile Safari, background discard).
  useEffect(() => {
    const flush = () => {
      void flushStepCheckpointPushes();
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, []);

  useLayoutEffect(() => {
    const nextStep = getStepFromPathname(location.pathname);
    if (!nextStep) return;

    const previousStep = previousStepRef.current;
    if (previousStep === nextStep) return;

    const transitionId = ++transitionIdRef.current;

    (async () => {
      await handleStepTransition(previousStep, nextStep);
      if (transitionId !== transitionIdRef.current) return;
      previousStepRef.current = nextStep;
    })();
  }, [location.pathname]);
}
